import { FlowType } from "@redotech/redo-model/return-flow";
import { StepType } from "@redotech/redo-return-ui/flows/step";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { FormSidePanel } from "@redotech/redo-web/form-builder/form-side-panel";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import * as flowEditorStyles from "../new-flow-editor.module.css";
import { categorizeSteps } from "../utils/step-type-mapping";

export interface WarningStep {
  title: string;
  tooltip?: string;
}

export interface FlowLeftPanelProps {
  stepOptions: StepType<any, any>[];
  flowType: FlowType;
  actionsSectionName?: string;
  warningSteps?: WarningStep[];
}

export const FlowLeftPanel = memo(function FlowLeftPanel({
  stepOptions,
  flowType,
  actionsSectionName,
}: FlowLeftPanelProps) {
  const categories = categorizeSteps(stepOptions, actionsSectionName);

  return (
    <FormSidePanel className={flowEditorStyles.leftPanel} side="left">
      <Flex dir="column" gap="3xl" pb="sm" pt="3xl" px="sm">
        <Flex dir="column" gap="sm">
          <Text fontWeight="semibold">Steps</Text>
          <Text color="tertiary" fontSize="sm">
            Create your automation by dragging and dropping steps into the map.
          </Text>
        </Flex>

        {categories.map((category, categoryIndex) => (
          <Flex dir="column" gap="md" key={categoryIndex}>
            <Text fontWeight="semibold">{category.name}</Text>
            <Flex dir="column" gap="xs">
              {category.steps.map(({ stepType, dragType }, stepIndex) => {
                const iconColor =
                  category.name === "Steps" ? "warning" : "success";

                const onDragStart = (event: React.DragEvent) => {
                  event.dataTransfer.setData("application/reactflow", dragType);
                  event.dataTransfer.setData("text/plain", dragType);
                  event.dataTransfer.effectAllowed = "move";

                  const containerElement = event.currentTarget as HTMLElement;
                  if (containerElement) {
                    const containerClone = containerElement.cloneNode(
                      true,
                    ) as HTMLElement;

                    containerClone.style.borderRadius = "6px";
                    containerClone.style.width = "224px";
                    containerClone.style.height = "48px";
                    containerClone.style.padding = "12px";
                    containerClone.style.background = "white";
                    containerClone.style.boxShadow =
                      "0px 2px 4px -1px rgba(16, 24, 40, 0.1)";

                    document.body.append(containerClone);
                    event.dataTransfer.setDragImage(containerClone, 112, 30);

                    setTimeout(() => {
                      containerClone.remove();
                    }, 0);
                  }
                };

                return (
                  <Flex
                    align="center"
                    draggable
                    gap="md"
                    key={stepIndex}
                    onDragStart={onDragStart}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "var(--redo-colors-gray-light-100)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    py="sm"
                    style={{
                      cursor: "move",
                      borderRadius: "4px",
                      padding: "8px",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <RedoFeaturedIcon
                      color={iconColor}
                      Icon={stepType.Icon}
                      size="sm"
                    />
                    <Text fontSize="sm">{stepType.title}</Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </FormSidePanel>
  );
});
